/* SEQ Air Care global booking modal controller */
'use strict';

(function initSeqGlobalBookingModal() {
  function start() {
    const dialog = document.querySelector('[data-seq-booking-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    const app = dialog.querySelector('[data-global-booking-app]');
    const hideShopifyCart = dialog.dataset.hideShopifyCart === 'true';
    const promotionsDisabled = Boolean(
      dialog.dataset.disablePromotions === 'true'
      || window.SEQ_DISABLE_PROMOTIONS === true
      || /^\/pages\/manage-booking\/?$/i.test(window.location.pathname)
    );
    const scrollArea = dialog.querySelector('[data-seq-booking-scroll]');
    const bookingHeader = dialog.querySelector('.seq-global-booking__header');
    const closeButtons = Array.from(dialog.querySelectorAll('[data-close-booking]'));
    const primaryCloseButton = closeButtons[0] || null;
    const reviewsOpenButton = dialog.querySelector('[data-open-booking-reviews]');
    const reviewsView = dialog.querySelector('[data-booking-reviews-view]');
    const reviewsScroll = dialog.querySelector('[data-booking-reviews-scroll]');
    const reviewsBackButtons = Array.from(dialog.querySelectorAll('[data-close-booking-reviews]'));
    const winterOfferTab = document.querySelector('[data-winter-offer-tab]');
    const winterOfferTabLabel = winterOfferTab?.querySelector('[data-winter-offer-tab-label]');
    const winterOfferTabCode = winterOfferTab?.querySelector('[data-winter-offer-tab-code]');
    let lastTrigger = null;
    let pendingTrigger = null;
    let bookingScrollPosition = 0;
    let reviewsAreOpen = false;
    const bookingLabelledBy = dialog.getAttribute('aria-labelledby') || '';
    const reviewsLabelledBy = reviewsView?.getAttribute('aria-labelledby') || '';
    const winterOfferDelayMs = 12000;
    const offerStorageKey = 'seq_booking_offer_state_v2';
    const offerSessionSeenKey = 'seq_booking_offer_seen_session_v3';
    const offerSessionStartKey = 'seq_booking_offer_session_started_v3';
    const offerResponseStorageKey = 'seq_booking_offer_response_session_v3';
    let winterOfferTimer = null;
    let winterOfferWaitingForVisibility = false;
    let winterOfferRequestInFlight = false;

    if (hideShopifyCart) {
      document.documentElement.classList.add('seq-direct-booking-hide-cart');

      if (/^\/cart\/?$/i.test(window.location.pathname)) {
        const destination = new URL('/', window.location.origin);
        destination.searchParams.set('open-booking', 'true');
        window.location.replace(destination.toString());
        return;
      }
    }

    const funnelEndpoint = String(dialog.dataset.funnelEndpoint || '').trim();
    const funnelEventNames = new Set([
      'booking_modal_opened',
      'service_quantity_changed',
      'appointment_step_opened',
      'suburb_selected',
      'date_selected',
      'slot_selected',
      'slot_reserved',
      'details_step_opened',
      'confirmation_attempted',
      'booking_confirmed',
      'booking_failed',
      'booking_modal_closed'
    ]);
    const trackingSessionKey = 'seq_booking_funnel_session_v1';
    const trackingLandingKey = 'seq_booking_funnel_landing_v1';
    let trackingContext = {};

    function trackingUuid() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
        const random = Math.random() * 16 | 0;
        const value = character === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
      });
    }

    function trackingSessionValue(key, fallback) {
      try {
        const existing = sessionStorage.getItem(key);
        if (existing) return existing;
        const value = typeof fallback === 'function' ? fallback() : fallback;
        sessionStorage.setItem(key, value);
        return value;
      } catch (error) {
        return typeof fallback === 'function' ? fallback() : fallback;
      }
    }

    const trackingSessionId = trackingSessionValue(trackingSessionKey, trackingUuid);
    const trackingLandingPage = trackingSessionValue(
      trackingLandingKey,
      () => `${window.location.origin}${window.location.pathname}${window.location.search}`
    );
    const trackingUrl = new URL(window.location.href);
    const trackingUtm = {
      utm_source: trackingUrl.searchParams.get('utm_source') || '',
      utm_medium: trackingUrl.searchParams.get('utm_medium') || '',
      utm_campaign: trackingUrl.searchParams.get('utm_campaign') || '',
      utm_content: trackingUrl.searchParams.get('utm_content') || '',
      utm_term: trackingUrl.searchParams.get('utm_term') || ''
    };

    function inferredTrackingSource() {
      if (trackingContext.source) return trackingContext.source;
      if (trackingUtm.utm_source) return trackingUtm.utm_source;
      try {
        return document.referrer ? new URL(document.referrer).hostname : 'direct';
      } catch (error) {
        return 'direct';
      }
    }

    function sendFunnelEvent(eventName, details = {}) {
      if (!funnelEndpoint || !funnelEventNames.has(eventName)) return;

      const payload = {
        eventId: trackingUuid(),
        sessionId: trackingSessionId,
        eventName,
        occurredAt: new Date().toISOString(),
        pageUrl: `${window.location.origin}${window.location.pathname}${window.location.search}`,
        landingPage: trackingLandingPage,
        source: details.source || inferredTrackingSource(),
        campaign: details.campaign || trackingContext.campaign || trackingUtm.utm_campaign || '',
        step: details.step ?? null,
        suburb: details.suburb || '',
        indoorUnits: details.indoorUnits ?? details.indoor_units ?? null,
        outdoorUnits: details.outdoorUnits ?? details.outdoor_units ?? null,
        valueCents: details.valueCents ?? details.value_cents ?? null,
        discountCode: details.discountCode || details.discount_code || '',
        bookingId: details.bookingId || details.booking_id || null,
        metadata: {
          ...(details.metadata || {}),
          trigger: details.trigger || trackingContext.trigger || '',
          requested_step: trackingContext.requestedStep || '',
          returning_customer: details.returningCustomer ?? details.returning_customer ?? '',
          rebooking: details.rebooking ?? trackingContext.rebooking ?? '',
          ...trackingUtm
        }
      };

      try {
        void fetch(funnelEndpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify(payload)
        }).catch(() => {});
      } catch (error) {
        // Analytics must never interrupt booking.
      }
    }

    const sharedTracking = window.SEQBookingTracking || {};
    sharedTracking.setContext = context => {
      trackingContext = { ...trackingContext, ...(context || {}) };
    };
    sharedTracking.track = (eventName, details = {}) => {
      const analyticsDetails = { ...details };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...analyticsDetails });
      if (typeof window.clarity === 'function') window.clarity('event', eventName);
      sendFunnelEvent(eventName, analyticsDetails);
    };
    sharedTracking.sessionId = trackingSessionId;
    window.SEQBookingTracking = sharedTracking;

    function track(eventName, details = {}) {
      window.SEQBookingTracking.track(eventName, details);
    }

    function readLocalValue(key) {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    function readSessionValue(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    function writeSessionValue(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {
        // Session storage can be unavailable in privacy-restricted browsers.
      }
    }

    function removeSessionValue(key) {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        // Session storage can be unavailable in privacy-restricted browsers.
      }
    }

    function hasAppliedWinterOffer() {
      return (
        readLocalValue(offerStorageKey) === 'applied' ||
        readLocalValue('seq_booking_offer_state_v1') === 'applied'
      );
    }

    function updateWinterOfferTab(
      applied = hasAppliedWinterOffer(),
      { reveal = null } = {}
    ) {
      if (!winterOfferTab) return;

      const offerHasBeenIntroduced = Boolean(
        applied || readSessionValue(offerSessionSeenKey)
      );
      const shouldReveal = reveal === null
        ? offerHasBeenIntroduced
        : Boolean(reveal);

      winterOfferTab.hidden = !shouldReveal;
      winterOfferTab.classList.toggle('is-applied', applied);
      winterOfferTab.dataset.offerApplied = applied ? 'true' : 'false';
      winterOfferTab.dataset.offerIntroduced = shouldReveal ? 'true' : 'false';

      if (winterOfferTabLabel) {
        winterOfferTabLabel.textContent = applied
          ? 'Discount Applied'
          : 'Apply $30 Off!';
      }

      if (winterOfferTabCode) {
        winterOfferTabCode.textContent = applied
          ? 'WINTER30 active'
          : 'WINTER30';
      }

      winterOfferTab.setAttribute(
        'aria-label',
        applied
          ? 'Winter discount applied. Open your booking.'
          : 'Apply the $30 Winter Special'
      );
    }

    function clearAppliedWinterOfferForTesting() {
      try {
        localStorage.removeItem(offerStorageKey);
        localStorage.removeItem('seq_booking_offer_state_v1');
      } catch (error) {
        // Storage can be unavailable in privacy-restricted browsers.
      }
      updateWinterOfferTab(false);
    }

    function hasActiveSavedReservation() {
      try {
        const raw = localStorage.getItem('seq_booking_draft_v1');
        if (!raw) return false;
        const draft = JSON.parse(raw);
        const expiry = draft?.reservationExpiresAt
          ? new Date(draft.reservationExpiresAt).getTime()
          : NaN;
        return Boolean(
          draft?.selectedBookingId &&
          Number.isFinite(expiry) &&
          expiry > Date.now()
        );
      } catch (error) {
        return false;
      }
    }

    function getTriggerOptions(trigger) {
      const options = {
        source: trigger?.dataset.bookingSource || '',
        suburb: (trigger?.dataset.bookingSuburb || '').trim(),
        units: trigger?.dataset.bookingUnits || '',
        outdoor: trigger?.dataset.bookingOutdoor || '',
        step: trigger?.dataset.bookingStep || '',
        campaign: (trigger?.dataset.bookingCampaign || '').trim(),
        discount: (trigger?.dataset.bookingDiscount || '').trim(),
        rebook: trigger?.dataset.bookingRebook || '',
        customerName: (trigger?.dataset.bookingName || '').trim(),
        customerMobile: (trigger?.dataset.bookingMobile || '').trim(),
        serviceAddress: (trigger?.dataset.bookingAddress || '').trim()
      };

      const href = trigger?.getAttribute?.('href');
      if (!href) return options;

      try {
        const url = new URL(href, window.location.href);
        const params = new URLSearchParams(url.search);
        const hashQueryIndex = url.hash.indexOf('?');
        if (hashQueryIndex !== -1) {
          const hashParams = new URLSearchParams(url.hash.slice(hashQueryIndex + 1));
          hashParams.forEach((value, key) => {
            if (!params.has(key)) params.set(key, value);
          });
        }
        if (!options.suburb) options.suburb = (params.get('suburb') || params.get('customer_suburb') || '').trim();
        if (!options.units) options.units = params.get('units') || params.get('indoor') || params.get('indoor_units') || '';
        if (!options.outdoor) options.outdoor = params.get('outdoor') || params.get('outdoor_units') || '';
        if (!options.step) options.step = params.get('step') || '';
        if (!options.campaign) options.campaign = params.get('campaign') || params.get('utm_campaign') || '';
        if (!options.discount) options.discount = params.get('discount') || params.get('promo') || params.get('code') || '';
        if (!options.rebook) options.rebook = params.get('rebook') || '';
      } catch (error) {
        // The real href remains the no-JavaScript fallback.
      }
      return options;
    }

    function setCount(value, countSelector, minusSelector, plusSelector, min, max) {
      const count = app?.querySelector(countSelector);
      const minus = app?.querySelector(minusSelector);
      const plus = app?.querySelector(plusSelector);
      if (!count || !minus || !plus || !Number.isFinite(value)) return;

      const wanted = Math.max(min, Math.min(max, value));
      let current = Number.parseInt(count.textContent || String(min), 10);
      if (!Number.isFinite(current)) current = min;

      let guard = 0;
      while (current < wanted && guard < 20) {
        plus.click(); current += 1; guard += 1;
      }
      while (current > wanted && guard < 40) {
        minus.click(); current -= 1; guard += 1;
      }
    }

    function applyTriggerData(trigger) {
      if (!app || !trigger) return;
      if (app.dataset.seqBookingInitialized !== 'true') {
        pendingTrigger = trigger;
        return;
      }

      pendingTrigger = null;
      const options = getTriggerOptions(trigger);
      const units = Number.parseInt(options.units || '', 10);
      const outdoor = Number.parseInt(options.outdoor || '', 10);

      if (Number.isFinite(units)) {
        setCount(units, '[data-unit-count]', '[data-action="minus"]', '[data-action="plus"]', 1, 10);
      }
      if (Number.isFinite(outdoor)) {
        const indoorCount = Number.parseInt(app.querySelector('[data-unit-count]')?.textContent || '1', 10);
        setCount(outdoor, '[data-outdoor-count]', '[data-outdoor-action="minus"]', '[data-outdoor-action="plus"]', 0, indoorCount);
      }
      if (options.suburb) {
        const select = app.querySelector('[data-suburb-select]');
        const matchingOption = Array.from(select?.options || []).find(option => option.value.toLowerCase() === options.suburb.toLowerCase());
        if (select && matchingOption && select.value !== matchingOption.value) {
          select.value = matchingOption.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      app.dispatchEvent(new CustomEvent('seq:booking-context', {
        bubbles: false,
        detail: options
      }));

      const requestedStep = Number.parseInt(options.step || '', 10);
      if (requestedStep === 2 && options.suburb) app.querySelector('[data-step-jump="2"]')?.click();
      else if (requestedStep === 1) app.querySelector('[data-step-jump="1"]')?.click();
    }

    function dispatchModalState(name) {
      const event = new CustomEvent(name, { bubbles: false });
      dialog.dispatchEvent(event);
      app?.dispatchEvent(new CustomEvent(name, { bubbles: false }));
    }


    function setBookingViewInteractive(isInteractive) {
      [bookingHeader, scrollArea].forEach(element => {
        if (!element) return;
        element.inert = !isInteractive;
        element.setAttribute('aria-hidden', isInteractive ? 'false' : 'true');
      });
    }

    function openReviews() {
      if (!reviewsView || reviewsAreOpen) return;

      bookingScrollPosition = scrollArea?.scrollTop || 0;
      reviewsAreOpen = true;
      reviewsView.hidden = false;
      reviewsView.setAttribute('aria-hidden', 'false');
      reviewsOpenButton?.setAttribute('aria-expanded', 'true');
      dialog.classList.add('is-reviews-open');
      dialog.dataset.reviewsOpen = 'true';
      setBookingViewInteractive(false);
      if (reviewsLabelledBy) dialog.setAttribute('aria-labelledby', reviewsLabelledBy);
      dialog.dispatchEvent(new CustomEvent('seq:reviews-opened', { bubbles: false }));
      dialog.dispatchEvent(new CustomEvent('seq:modal-closing', { bubbles: false }));

      if (reviewsScroll) reviewsScroll.scrollTop = 0;
      window.requestAnimationFrame(() => {
        reviewsView.classList.add('is-open');
        reviewsView.querySelector('[data-close-booking-reviews]')?.focus({ preventScroll: true });
      });

      track('seq_global_booking_reviews_open', {
        source: 'modal_header',
        booking_step: app?.querySelector('.seq-step-panel.is-active')?.dataset.stepPanel || ''
      });
    }

    function closeReviews({ restoreFocus = true, restartOfferTimer = true } = {}) {
      if (!reviewsView || !reviewsAreOpen) return;

      reviewsAreOpen = false;
      reviewsView.classList.remove('is-open');
      reviewsView.setAttribute('aria-hidden', 'true');
      reviewsOpenButton?.setAttribute('aria-expanded', 'false');
      dialog.classList.remove('is-reviews-open');
      delete dialog.dataset.reviewsOpen;
      setBookingViewInteractive(true);
      if (bookingLabelledBy) dialog.setAttribute('aria-labelledby', bookingLabelledBy);
      dialog.dispatchEvent(new CustomEvent('seq:reviews-closed', { bubbles: false }));

      window.setTimeout(() => {
        if (!reviewsAreOpen) reviewsView.hidden = true;
      }, 220);

      if (scrollArea) scrollArea.scrollTop = bookingScrollPosition;
      if (restartOfferTimer && dialog.open) dispatchModalState('seq:modal-opened');
      if (restoreFocus) reviewsOpenButton?.focus({ preventScroll: true });

      track('seq_global_booking_reviews_close', { reason: 'back_to_booking' });
    }

    let cartBookingView = null;
    let currentCartBooking = null;
    let cartCheckRequestId = 0;

    function escapeHtml(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    function formatCartMoney(cents) {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 0
      }).format(Number(cents || 0) / 100);
    }

    function propertyFrom(item, name) {
      const value = item?.properties?.[name];
      return value === undefined || value === null ? '' : String(value);
    }

    function parseCartBooking(cart) {
      const bookingItems = (cart?.items || []).filter(item => propertyFrom(item, 'Booking ID'));
      if (!bookingItems.length) return null;

      const primary = bookingItems.find(item => propertyFrom(item, 'Service') === 'Split System Deep Clean') || bookingItems[0];
      const properties = primary.properties || {};
      const bookingId = propertyFrom(primary, 'Booking ID');
      const matchingItems = bookingItems.filter(item => propertyFrom(item, 'Booking ID') === bookingId);
      const totalPrice = matchingItems.reduce((sum, item) => sum + Number(item.final_line_price ?? item.line_price ?? 0), 0);
      const expiresAt = propertyFrom(primary, 'Reservation Expires At');
      const expiresTime = expiresAt ? new Date(expiresAt).getTime() : NaN;

      return {
        bookingId,
        manageBookingToken: propertyFrom(primary, 'Manage Booking Token'),
        bookingDate: propertyFrom(primary, 'Booking Date'),
        bookingTime: propertyFrom(primary, 'Booking Time'),
        bookingEndTime: propertyFrom(primary, 'Booking End Time'),
        bookingStartISO: propertyFrom(primary, 'Booking Start ISO'),
        bookingEndISO: propertyFrom(primary, 'Booking End ISO'),
        reservationExpiresAt: expiresAt,
        expired: Number.isFinite(expiresTime) && expiresTime <= Date.now(),
        suburb: propertyFrom(primary, 'Suburb'),
        indoorUnits: Number(propertyFrom(primary, 'Indoor Units') || 1),
        outdoorUnits: Number(propertyFrom(primary, 'Outdoor Units') || 0),
        mobile: propertyFrom(primary, 'Customer Mobile'),
        winterSpecial: /applied/i.test(propertyFrom(primary, 'Winter Special')),
        totalPrice,
        itemKeys: matchingItems.map(item => item.key),
        properties
      };
    }

    function ensureCartBookingView() {
      if (cartBookingView) return cartBookingView;
      cartBookingView = document.createElement('section');
      cartBookingView.className = 'seq-cart-booking-view';
      cartBookingView.dataset.cartBookingView = '';
      cartBookingView.hidden = true;
      scrollArea?.appendChild(cartBookingView);
      return cartBookingView;
    }

    function showBookingForm() {
      ensureCartBookingView().hidden = true;
      if (app) app.hidden = false;
    }

    function showCartLoading() {
      const view = ensureCartBookingView();
      if (app) app.hidden = true;
      view.hidden = false;
      view.innerHTML = `
        <div class="seq-cart-booking-loading" role="status" aria-live="polite">
          <span aria-hidden="true"></span>
          <strong>Checking your saved booking…</strong>
        </div>
      `;
    }

    function renderCartBooking(booking) {
      currentCartBooking = booking;
      const view = ensureCartBookingView();
      if (app) app.hidden = true;
      view.hidden = false;

      const unitSummary = [
        `${booking.indoorUnits} indoor unit${booking.indoorUnits === 1 ? '' : 's'}`,
        booking.outdoorUnits > 0 ? `${booking.outdoorUnits} outdoor unit${booking.outdoorUnits === 1 ? '' : 's'}` : ''
      ].filter(Boolean).join(' · ');

      const statusCopy = booking.expired
        ? 'Your appointment hold has expired'
        : 'Your appointment is saved';
      const statusText = booking.expired
        ? 'Your service details are still here, but this time is no longer reserved. Choose another appointment to continue.'
        : 'You already have a booking waiting in checkout. Continue when you are ready, or edit it here.';

      view.innerHTML = `
        <div class="seq-cart-booking-card ${booking.expired ? 'is-expired' : ''}">
          <div class="seq-cart-booking-status">
            <span class="seq-cart-booking-status__icon" aria-hidden="true">${booking.expired ? '!' : '✓'}</span>
            <div>
              <span>Current booking</span>
              <h2>${escapeHtml(statusCopy)}</h2>
              <p>${escapeHtml(statusText)}</p>
            </div>
          </div>

          <div class="seq-cart-booking-summary">
            <div class="seq-cart-booking-appointment">
              <span>Appointment</span>
              <strong>${escapeHtml(booking.bookingDate || 'Date saved')}</strong>
              <small>${escapeHtml(booking.bookingTime || '')}</small>
            </div>
            <div><span>Suburb</span><strong>${escapeHtml(booking.suburb || '—')}</strong></div>
            <div><span>Service</span><strong>${escapeHtml(unitSummary)}</strong></div>
            <div><span>Total</span><strong>${escapeHtml(formatCartMoney(booking.totalPrice))}</strong></div>
          </div>

          <div class="seq-cart-booking-actions">
            ${booking.expired
              ? '<button type="button" class="seq-cart-booking-primary" data-cart-booking-edit>Choose another appointment</button>'
              : '<a class="seq-cart-booking-primary" href="/checkout" data-cart-booking-checkout>Continue to checkout</a>'}
            ${booking.expired ? '' : '<button type="button" class="seq-cart-booking-secondary" data-cart-booking-edit>Edit appointment</button>'}
            <button type="button" class="seq-cart-booking-remove" data-cart-booking-remove>Remove booking</button>
          </div>

          <div class="seq-cart-booking-reassurance">
            <span>Nothing charged today</span>
            <span>Pay after service</span>
            <span>Your details stay saved</span>
          </div>
        </div>
      `;
    }

    async function readCartBooking() {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Could not read Shopify cart');
      return parseCartBooking(await response.json());
    }

    function isScheduledWinterOfferEligible() {
      return Boolean(
        !hasAppliedWinterOffer() &&
        !readSessionValue(offerSessionSeenKey)
      );
    }

    function canAttemptScheduledWinterOffer() {
      // Phase 2: never interrupt an active visitor with a timed promotional overlay.
      // Campaign discounts are applied or offered inline inside step one instead.
      return false;
    }

    async function showScheduledWinterOffer({
      force = false,
      source = 'scheduled_winter_offer'
    } = {}) {
      if (!canAttemptScheduledWinterOffer({ force })) return false;

      if (document.hidden) {
        winterOfferWaitingForVisibility = true;
        return;
      }

      winterOfferWaitingForVisibility = false;
      winterOfferRequestInFlight = true;

      try {
        if (!force && !isScheduledWinterOfferEligible()) return false;

        const wasAlreadyOpen = dialog.open;
        if (!wasAlreadyOpen) {
          lastTrigger = document.activeElement;
          dialog.dataset.offerAutoOpened = 'true';
          dialog.showModal();
          if (reviewsAreOpen) closeReviews({ restoreFocus: false, restartOfferTimer: false });
          setBookingViewInteractive(true);
          document.documentElement.classList.add('seq-global-booking-open');
          if (scrollArea) scrollArea.scrollTop = 0;
          showBookingForm();
          dispatchModalState('seq:modal-opened');
          track('seq_global_booking_open', { source });
        } else {
          delete dialog.dataset.offerAutoOpened;
          if (reviewsAreOpen) closeReviews({ restoreFocus: false, restartOfferTimer: false });
        }

        let shown = false;
        const receiveResult = event => {
          shown = Boolean(event.detail?.shown);
        };
        dialog.addEventListener('seq:offer-show-result', receiveResult, { once: true });
        dialog.dispatchEvent(new CustomEvent('seq:offer-show-requested', {
          bubbles: false,
          detail: {
            force,
            trigger: source === 'scheduled_winter_offer'
              ? 'fixed_12_second'
              : source
          }
        }));

        if (shown) {
          updateWinterOfferTab(hasAppliedWinterOffer(), { reveal: true });
        } else if (!wasAlreadyOpen && dialog.open) {
          delete dialog.dataset.offerAutoOpened;
          close('offer_not_eligible');
        }

        return shown;
      } catch (error) {
        console.warn('Could not show the Winter Special popup.', error);
        return false;
      } finally {
        winterOfferRequestInFlight = false;
      }
    }

    function scheduleWinterOffer() {
      if (winterOfferTimer) window.clearTimeout(winterOfferTimer);
      winterOfferTimer = null;
      if (!canAttemptScheduledWinterOffer()) return;

      let sessionStartedAt = Number(readSessionValue(offerSessionStartKey) || 0);
      if (!Number.isFinite(sessionStartedAt) || sessionStartedAt <= 0) {
        sessionStartedAt = Date.now();
        writeSessionValue(offerSessionStartKey, String(sessionStartedAt));
      }

      const delay = Math.max(0, winterOfferDelayMs - (Date.now() - sessionStartedAt));
      winterOfferTimer = window.setTimeout(() => {
        winterOfferTimer = null;
        void showScheduledWinterOffer();
      }, delay);
    }

    async function checkCartBeforeOpening(trigger) {
      const requestId = ++cartCheckRequestId;
      showCartLoading();
      try {
        const booking = await readCartBooking();
        if (requestId !== cartCheckRequestId) return;
        if (booking) {
          renderCartBooking(booking);
          track('seq_cart_booking_open', { booking_id: booking.bookingId, expired: booking.expired });
          return;
        }
      } catch (error) {
        console.warn('Could not check the current booking cart.', error);
      }

      if (requestId !== cartCheckRequestId) return;
      currentCartBooking = null;
      showBookingForm();
      applyTriggerData(trigger);
      dispatchModalState('seq:modal-opened');
    }

    function editCartBooking() {
      if (!currentCartBooking) return;
      const booking = currentCartBooking;
      showBookingForm();
      app?.dispatchEvent(new CustomEvent('seq:load-cart-booking', {
        bubbles: false,
        detail: { ...booking, chooseNewTime: booking.expired }
      }));
      dispatchModalState('seq:modal-opened');
      track('seq_cart_booking_edit', { booking_id: booking.bookingId, expired: booking.expired });
    }

    async function removeCartBooking(button) {
      if (!currentCartBooking || button?.disabled) return;
      const confirmed = window.confirm('Remove this appointment from checkout? The reserved time will be released.');
      if (!confirmed) return;

      const booking = currentCartBooking;
      if (button) {
        button.disabled = true;
        button.textContent = 'Removing…';
      }

      try {
        if (window.SEQBookingEngine?.removeCartBooking) {
          await window.SEQBookingEngine.removeCartBooking(booking);
        } else {
          for (const key of booking.itemKeys || []) {
            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 })
            });
            if (!response.ok) throw new Error('Could not remove booking item');
          }
        }

        currentCartBooking = null;
        showBookingForm();
        app?.dispatchEvent(new CustomEvent('seq:cart-booking-removed', { bubbles: false }));
        dispatchModalState('seq:modal-opened');
        track('seq_cart_booking_removed', { booking_id: booking.bookingId });
      } catch (error) {
        console.error(error);
        window.alert('We could not remove the booking. Please try again.');
        if (button) {
          button.disabled = false;
          button.textContent = 'Remove booking';
        }
      }
    }

    async function open(trigger) {
      lastTrigger = trigger || document.activeElement;
      delete dialog.dataset.offerAutoOpened;
      if (!dialog.open) dialog.showModal();
      if (reviewsAreOpen) closeReviews({ restoreFocus: false, restartOfferTimer: false });
      setBookingViewInteractive(true);
      document.documentElement.classList.add('seq-global-booking-open');
      if (scrollArea) scrollArea.scrollTop = 0;
      primaryCloseButton?.focus({ preventScroll: true });

      const options = getTriggerOptions(trigger);
      const trackingSource = options.source || trigger?.textContent?.trim().replace(/\s+/g, ' ').slice(0, 80) || 'unknown';
      const isRebooking = ['1', 'true', 'yes'].includes(String(options.rebook || '').toLowerCase());
      window.SEQBookingTracking?.setContext({
        source: trackingSource,
        campaign: options.campaign || '',
        trigger: trigger?.dataset?.bookingSource || trigger?.tagName?.toLowerCase() || 'unknown',
        requestedStep: options.step || '',
        rebooking: isRebooking
      });
      track('booking_modal_opened', {
        source: trackingSource,
        campaign: options.campaign || '',
        suburb: options.suburb || '',
        indoorUnits: Number.parseInt(options.units, 10) || null,
        outdoorUnits: Number.parseInt(options.outdoor, 10) || 0,
        discountCode: options.discount || '',
        rebooking: isRebooking,
        metadata: { requested_step: options.step || '' }
      });
      track('seq_global_booking_open', {
        source: trackingSource,
        suburb: options.suburb || '',
        units: options.units || '',
        outdoor: options.outdoor || '',
        campaign: options.campaign || '',
        discount: options.discount || '',
        rebook: isRebooking
      });

      // Direct bookings are confirmed inside the modal. Ignore any legacy
      // Shopify cart booking and always reopen the saved modal/Supabase draft.
      cartCheckRequestId += 1;
      currentCartBooking = null;
      if (cartBookingView) cartBookingView.hidden = true;
      if (app) app.hidden = false;
      applyTriggerData(trigger);
      dispatchModalState('seq:modal-opened');
    }

    function close(reason = 'close_button') {
      if (!dialog.open) return;
      dispatchModalState('seq:modal-closing');
      dialog.close(reason);
    }

    winterOfferTab?.addEventListener('click', () => {
      const applied = hasAppliedWinterOffer();
      updateWinterOfferTab(applied);

      track('seq_winter_offer_tab_click', {
        state: applied ? 'applied' : 'available'
      });

      if (applied) {
        void open(winterOfferTab);
        return;
      }

      void showScheduledWinterOffer({
        force: true,
        source: 'winter_offer_tab'
      });
    });

    dialog.addEventListener('seq:offer-state-changed', event => {
      updateWinterOfferTab(Boolean(event.detail?.applied));
    });

    window.addEventListener('storage', event => {
      if (
        event.key === offerStorageKey ||
        event.key === 'seq_booking_offer_state_v1'
      ) {
        updateWinterOfferTab();
      }
    });

    document.addEventListener('click', event => {
      const linkedElement = event.target.closest('a[href]');
      if (hideShopifyCart && linkedElement) {
        try {
          const linkedUrl = new URL(linkedElement.href, window.location.href);
          if (
            linkedUrl.origin === window.location.origin &&
            (/^\/cart\/?$/i.test(linkedUrl.pathname) || /^\/checkout\/?$/i.test(linkedUrl.pathname))
          ) {
            event.preventDefault();
            void open(linkedElement);
            return;
          }
        } catch (error) {
          // Ignore malformed links and allow the browser's normal behaviour.
        }
      }

      const trigger = event.target.closest('[data-open-booking]');
      if (!trigger) return;
      event.preventDefault();
      void open(trigger);
    });

    dialog.addEventListener('click', event => {
      const editButton = event.target.closest('[data-cart-booking-edit]');
      if (editButton) {
        event.preventDefault();
        editCartBooking();
        return;
      }

      const removeButton = event.target.closest('[data-cart-booking-remove]');
      if (removeButton) {
        event.preventDefault();
        void removeCartBooking(removeButton);
      }
    });

    closeButtons.forEach(button => {
      button.addEventListener('click', () => close('close_button'));
    });

    reviewsOpenButton?.addEventListener('click', openReviews);
    reviewsBackButtons.forEach(button => {
      button.addEventListener('click', () => closeReviews());
    });

    dialog.addEventListener('seq:offer-auto-dismissed', () => {
      if (dialog.dataset.offerAutoOpened !== 'true') return;
      delete dialog.dataset.offerAutoOpened;
      close('offer_dismissed');
    });

    dialog.addEventListener('cancel', event => {
      event.preventDefault();
      if (dialog.classList.contains('seq-offer-open')) {
        dialog.dispatchEvent(new CustomEvent('seq:offer-close-requested', { bubbles: false }));
        return;
      }
      if (reviewsAreOpen) {
        closeReviews();
        return;
      }
      close('escape');
    });

    dialog.addEventListener('close', () => {
      delete dialog.dataset.offerAutoOpened;
      if (reviewsAreOpen) closeReviews({ restoreFocus: false, restartOfferTimer: false });
      setBookingViewInteractive(true);
      document.documentElement.classList.remove('seq-global-booking-open');
      updateWinterOfferTab();
      dispatchModalState('seq:modal-closed');
      track('booking_modal_closed', {
        metadata: { reason: dialog.returnValue || 'unknown' }
      });
      track('seq_global_booking_close', { reason: dialog.returnValue || 'unknown' });
      if (lastTrigger instanceof HTMLElement && document.contains(lastTrigger)) {
        lastTrigger.focus({ preventScroll: true });
      }
    });

    dialog.addEventListener('seq:booking-ready', () => {
      if (pendingTrigger) applyTriggerData(pendingTrigger);
      if (dialog.open) dispatchModalState('seq:modal-opened');
    });

    const initialUrl = new URL(window.location.href);
    if (initialUrl.searchParams.get('open-booking') === 'true') {
      const syntheticTrigger = document.createElement('a');
      syntheticTrigger.dataset.bookingSource = 'cart_redirect';
      syntheticTrigger.setAttribute('href', window.location.href);
      void open(syntheticTrigger);
      initialUrl.searchParams.delete('open-booking');
      window.history.replaceState({}, '', `${initialUrl.pathname}${initialUrl.search}${initialUrl.hash}`);
    } else if (window.location.hash.startsWith('#check-availability')) {
      const syntheticTrigger = document.createElement('a');
      syntheticTrigger.dataset.bookingSource = 'url_hash';
      syntheticTrigger.setAttribute('href', window.location.href);
      void open(syntheticTrigger);
    }

    document.addEventListener('visibilitychange', () => {
      if (!promotionsDisabled && !document.hidden && winterOfferWaitingForVisibility) {
        void showScheduledWinterOffer();
      }
    });

    window.SEQBookingModal = {
      open(options = {}) {
        const syntheticTrigger = document.createElement('button');
        syntheticTrigger.dataset.bookingSource = options.source || 'javascript';
        if (options.suburb) syntheticTrigger.dataset.bookingSuburb = options.suburb;
        if (options.units) syntheticTrigger.dataset.bookingUnits = String(options.units);
        if (options.outdoor !== undefined) syntheticTrigger.dataset.bookingOutdoor = String(options.outdoor);
        if (options.step) syntheticTrigger.dataset.bookingStep = String(options.step);
        open(syntheticTrigger);
      },
      close,
      openReviews,
      closeReviews,
      resetIdlePopupForTesting() {
        [offerSessionSeenKey, offerSessionStartKey, offerResponseStorageKey].forEach(removeSessionValue);
        dialog.dispatchEvent(new CustomEvent('seq:offer-reset', { bubbles: false }));
        if (!promotionsDisabled) scheduleWinterOffer();
      },
      resetWinterOfferForTesting() {
        [offerSessionSeenKey, offerSessionStartKey, offerResponseStorageKey].forEach(removeSessionValue);
        clearAppliedWinterOfferForTesting();
        dialog.dispatchEvent(new CustomEvent('seq:offer-reset', { bubbles: false }));
        if (!promotionsDisabled) scheduleWinterOffer();
      }
    };

    if (winterOfferTimer) window.clearTimeout(winterOfferTimer);
    winterOfferTimer = null;
    winterOfferWaitingForVisibility = false;
    if (winterOfferTab) winterOfferTab.hidden = true;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();